import React, { useRef, useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { assignOffice, getEmployees } from "../../../core/data/redux/employeesSlice";
import { getOfficeLocation } from "../../../core/data/redux/officeLocationSlice";
import { useDispatch, useSelector } from "react-redux";


interface OfficeLocationsListProps {
    employee: any
}

const Offices = (props: OfficeLocationsListProps) => {

    const { employee } = props;
    const tableRef = useRef<{ resetSelection: ([]) => void }>(null);
    const dispatch = useDispatch();
    const officeLocationState = useSelector((state: any) => state.officeLocationState);
    const employeesState = useSelector((state: any) => state.employeesState);
    const [dataList, setDataList] = React.useState<any[]>([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 10
    });
    const [selectedRows, setSelectedRows] = useState<any[] | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [debouncedQuery, setDebouncedQuery] = useState<string>("");
    // Debounce the search query by 500ms
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(searchQuery);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const handleReset = (data: any[]) => {
        if (tableRef.current) {
            tableRef.current.resetSelection(data); // Call child's function
        }
    };


    React.useEffect(() => {
        if (employee) {
            dispatch(getOfficeLocation({ 
                page: 1, 
                limit: pagination.pageSize,
                orderColumn: "",
                orderBy: "",
                query: debouncedQuery,
                filters: "",
                is_verified: "",
                date_from: "",
                is_Active: "",
                date_to: "",
                month: "",
                year: ""
            }));
        }
    }, [employee, debouncedQuery]);

    const handleAssign = async () => {
        if (selectedRows && selectedRows.length > 0) {
            const payload = {
                employee_id: employee.id,
                office_location_ids: selectedRows
            };
            await dispatch(assignOffice(payload));
        }
    }

    React.useEffect(() => {
        if (employeesState.loading === "fulfilled" && employeesState.action === "assign-office") {
            dispatch(getEmployees({
                page: 1,
                limit: 10,
                query: ""
            }));
        }
    }, [employeesState.loading, employeesState.action]);

    React.useEffect(() => {
        const assignedOffices = employee?.assigned_offices || employee?.offices || [];
        const selectedOfficeIds = assignedOffices.map((item: any) => item.office_location_id);
        handleReset(selectedOfficeIds);
        setSelectedRows(selectedOfficeIds);
    }, [employee]);

    React.useEffect(() => {
        if (officeLocationState.loading === "fulfilled" && officeLocationState.action === "list") {
            const result = officeLocationState?.data || null;
            if (result && result.data && result.data.length > 0) {

                let officeLocations = result.data.map((item: any) => ({
                    id: item.id,
                    name: item.name,
                    address: item.address,
                    phone: item.phone || "",
                    email: item.email || "",
                    key: item.id,
                    status: item.status
                }));
                setDataList(officeLocations);
                setPagination({
                    ...pagination,
                    total: result.total || officeLocations.length,
                    current: result.page || 1,
                    pageSize: result.limit || 10
                });
            } else {
                setDataList([]);
            }
        }
    }, [officeLocationState.loading, officeLocationState.action]);





    const columns = [
        {
            title: "Sr. No.",
            dataIndex: "id",
            width: 100,
            render: (text: string, record: any, index: number) => (
                <>
                    <div>
                        {(pagination.current) ? (index + pagination.current * pagination.pageSize - pagination.pageSize + 1) : (index + 1)}
                    </div>
                </>
            ),
            sorter: (a: any, b: any) => a.id.length - b.id.length,
        },

        {
            title: "Name",
            dataIndex: "name",
            width: 200,
            onCell: () => ({
                style: {
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                },
            }),
            sorter: (a: any, b: any) => a.name.length - b.name.length,
        },
        {
            title: "Address",
            dataIndex: "address",
            width: 200,
            onCell: () => ({
                style: {
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                },
            }),
            sorter: (a: any, b: any) => a.address.length - b.address.length,
        },
        {
            title: "Phone",
            dataIndex: "phone",
            sorter: (a: any, b: any) =>
                a.phone.length - b.phone.length,
        },
        {
            title: "Email",
            dataIndex: "email",
            sorter: (a: any, b: any) =>
                a.email.length - b.email.length,
        },


    ];


    const handlePagination = (paginationData: any) => {
        // Ant Design table passes { current, pageSize } in onChange
        // The "Row Per Page" dropdown also passes { current: 1, pageSize: value }
        const page = paginationData.current ?? paginationData.page ?? 1;
        const pageSize = paginationData.pageSize ?? pagination.pageSize;
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize,
        }));
        dispatch(getOfficeLocation({ 
            page: page, 
            limit: pageSize,
            orderColumn: "",
            orderBy: "",
            query: searchQuery,
            filters: "",
            is_verified: "",
            date_from: "",
            is_Active: "",
            date_to: "",
            month: "",
            year: ""
        }));
    };

    const handleSearch = useCallback((value: string) => {
        setSearchQuery(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    }, []);

    const handleOnRowSelection = (selectedRowKeys: any) => {
        setSelectedRows(selectedRowKeys);
        //setStatus(null);
    };



    return (<div>
        <>
            <div className="pt-2">
                <Table
                    columns={columns}
                    dataSource={dataList}
                    Selection={true}
                    paginationConfig={pagination}
                    handlePagination={handlePagination}
                    loading={officeLocationState.loading === "pending"}
                    onRowSelection={handleOnRowSelection}
                    handleSearch={handleSearch}
                    searchPlaceholder="Search by name or address..."
                    ref={tableRef}
                    scroll={{ y: 300 }}
                />
            </div>
            <div className="row align-items-center">
                <div className="col-md-12">
                    <div className="datatable-paginate mt-4" />
                    <div className="d-flex justify-content-center pb-3 pt-0">
                        {selectedRows && selectedRows.length > 0 ?
                            <Link to="#" data-bs-dismiss="modal" className="btn btn-primary" onClick={() => handleAssign()}>
                                Assign
                            </Link>

                            : <Link to="#" data-bs-dismiss="modal" className="btn btn-primary">
                                Select to assign
                            </Link>
                        }
                    </div>
                </div>
            </div>
        </>
    </div>
    );
}

export default Offices; 